/**
 * Référentiel des statuts juridiques et régimes fiscaux français
 * (simplifié pour le contexte HST — fidèle à la réalité du transport en formation).
 *
 * Modélisation gameplay :
 *   - capitalMin : capital social à débloquer à la création (en €)
 *   - liabilityLimited : si false, la trésorerie peut tomber en négatif et
 *     menacer les biens du dirigeant (faillite). Si true, responsabilité limitée
 *     au capital social → la société peut être liquidée sans toucher au dirigeant.
 *   - dirigeantRegime : 'tns' (Travailleur Non Salarié, ~40% charges sociales sur
 *     rémunération) ou 'salarie' (assimilé salarié, ~80% côté patronal sur rémunération)
 *   - banqueRateModifier : modificateur appliqué au taux d'emprunt (en points pct).
 *     Négatif = plus crédible (taux réduit). SAS = -0.5%, Auto-entrepreneur = +1%
 *   - startupAvailable : true si le statut peut être choisi au démarrage
 *   - cacRequired : true si commissaire aux comptes obligatoire au-delà des seuils
 *   - paliersCA : seuils de chiffre d'affaires glissant sur 12 mois qui déclenchent
 *     une évolution automatique au 01/01 suivant.
 */

export const LEGAL_FORMS = {
  auto: {
    id: 'auto',
    name: 'Auto-entrepreneur',
    shortName: 'Auto',
    fullLabel: 'Auto-entrepreneur (micro-entreprise)',
    description: 'Statut le plus simple. Aucun capital, comptabilité allégée. Idéal pour démarrer seul, mais plafonné rapidement.',
    capitalMin: 0,
    liabilityLimited: false,
    dirigeantRegime: 'tns',
    chargesSocialesPct: 0.22, // 22% du CA encaissé (régime micro-social simplifié)
    banqueRateModifier: 1.0,  // +1% sur le taux d'emprunt (peu crédible)
    startupAvailable: true,
    cacRequired: false,
    paliersCA: { transport: 188700 }, // seuil micro-BIC vente, dépassement = bascule en EI réel
    color: '#0EA5E9',
    icon: '👤',
    teachingNote: 'Le statut Auto-entrepreneur convient parfaitement pour débuter une activité de transport léger (moins de 3,5T). Les charges sont prélevées proportionnellement au CA (22%), sans avance. ATTENTION : non-cumulable avec une activité de transport lourd. Plafond : 188 700 € de CA/an. Au-delà, bascule obligatoire en EI au réel.',
  },
  ei: {
    id: 'ei',
    name: 'Entreprise Individuelle',
    shortName: 'EI',
    fullLabel: 'Entreprise Individuelle (EI)',
    description: 'Activité en nom propre, sans personnalité morale. Patrimoine de l\'entreprise et personnel désormais séparés (loi 2022).',
    capitalMin: 0,
    liabilityLimited: false, // Avant 2022 illimitée ; depuis loi Indépendants : limitée au patrimoine pro
    dirigeantRegime: 'tns',
    chargesSocialesPct: 0.40, // ~40% sur le bénéfice
    banqueRateModifier: 0.5,
    startupAvailable: true,
    cacRequired: false,
    paliersCA: { transport: 840000 }, // seuil régime simplifié → réel normal
    color: '#3B82F6',
    icon: '🚚',
    teachingNote: 'L\'EI est le statut transitoire le plus courant entre auto-entrepreneur et société. Pas de capital, mais cotisations TNS (Travailleur Non Salarié) ~40% du bénéfice. Depuis 2022, le patrimoine pro est protégé du patrimoine personnel.',
  },
  eurl: {
    id: 'eurl',
    name: 'EURL',
    shortName: 'EURL',
    fullLabel: 'Entreprise Unipersonnelle à Responsabilité Limitée',
    description: 'SARL à associé unique. Personnalité morale, responsabilité limitée au capital social.',
    capitalMin: 1, // Minimum légal 1€ (mais 1000€ recommandé pour crédibilité)
    capitalRecommended: 1000,
    liabilityLimited: true,
    dirigeantRegime: 'tns',
    chargesSocialesPct: 0.40,
    banqueRateModifier: 0,
    startupAvailable: true,
    cacRequired: false,
    paliersCA: { transport: 840000 },
    color: '#7E22CE',
    icon: '🏢',
    teachingNote: 'L\'EURL apporte la responsabilité limitée tout en gardant la simplicité de gestion (1 seul associé). Gérant TNS (40% cotisations) mais peut opter pour IS. Convient à une activité de transport en pleine croissance avec besoin de protéger son patrimoine personnel.',
  },
  sarl: {
    id: 'sarl',
    name: 'SARL',
    shortName: 'SARL',
    fullLabel: 'Société À Responsabilité Limitée',
    description: 'Société commerciale avec 2 à 100 associés. Responsabilité limitée au capital social.',
    capitalMin: 7500, // pas d'obligation légale mais 7500€ usuel pour le secteur transport
    capitalRecommended: 7500,
    liabilityLimited: true,
    dirigeantRegime: 'tns', // gérant majoritaire = TNS
    chargesSocialesPct: 0.40,
    banqueRateModifier: -0.25,
    startupAvailable: false,
    cacRequired: true, // au-delà de seuils
    paliersCA: { transport: 840000 },
    color: '#DC2626',
    icon: '🏛️',
    teachingNote: 'La SARL est la forme classique pour s\'associer (2 à 100 associés). Gérant majoritaire = TNS (~40%), minoritaire = assimilé salarié (~80% patronal). CAC obligatoire au-delà de 2 des 3 seuils : 4M€ bilan / 8M€ CA / 50 salariés. Réservée à une croissance multi-associés.',
  },
  sas: {
    id: 'sas',
    name: 'SAS / SASU',
    shortName: 'SAS',
    fullLabel: 'Société par Actions Simplifiée',
    description: 'Société à actions, gouvernance souple, fiscalité IS, président assimilé salarié.',
    capitalMin: 1, // libre, 1€ légal mais 10k€+ recommandé secteur transport
    capitalRecommended: 10000,
    liabilityLimited: true,
    dirigeantRegime: 'salarie', // président SAS = assimilé salarié, ~80% côté patronal
    chargesSocialesPct: 0.80,
    banqueRateModifier: -0.5,
    startupAvailable: false,
    cacRequired: true,
    paliersCA: { transport: 840000 },
    color: '#059669',
    icon: '🏰',
    teachingNote: 'La SAS / SASU offre la plus grande souplesse statutaire et la meilleure crédibilité bancaire (-0.5% sur le taux d\'emprunt). Président = assimilé salarié (~80% côté patronal sur rémunération versée) mais sans cotisation TNS forfaitaire. Adaptée aux structures qui visent une levée de fonds ou un développement rapide.',
  },
};

export const FISCAL_REGIMES = {
  micro: {
    id: 'micro',
    name: 'Micro-BIC',
    shortName: 'Micro',
    description: 'Bénéfices Industriels et Commerciaux — régime simplifié auto-entrepreneur. Abattement forfaitaire 50% pour transport.',
    requires: ['auto'],
    seuilCA: 188700, // services transport
    isRate: 0, // les charges sociales TNS sont libératoires de l'IR
    teachingNote: 'Régime ultra-simplifié réservé aux Auto-entrepreneurs. Pas de comptabilité, déclaration trimestrielle ou mensuelle. Abattement forfaitaire = pas de déduction des charges réelles.',
  },
  simplifie: {
    id: 'simplifie',
    name: 'Réel Simplifié',
    shortName: 'Réel simpl.',
    description: 'Comptabilité complète. Déduction des charges réelles. Obligatoire entre 188 700 € et 840 000 € de CA transport.',
    requires: ['ei', 'eurl', 'sarl', 'sas'],
    seuilCA: 840000,
    isRate: 0.15, // 15% jusqu'à 42 500 € de bénéfice (PME)
    isRateAbove: 0.25, // 25% au-delà
    teachingNote: 'Le régime le plus courant pour PME transport. Bilan + compte de résultat simplifiés. TVA déclarée trimestriellement. IS à 15% jusqu\'à 42 500 € de bénéfice, 25% au-delà.',
  },
  normal: {
    id: 'normal',
    name: 'Réel Normal',
    shortName: 'Réel normal',
    description: 'Comptabilité complète avec liasse fiscale complète. Obligatoire au-delà de 840 000 € de CA transport.',
    requires: ['ei', 'eurl', 'sarl', 'sas'],
    seuilCA: Infinity,
    isRate: 0.15,
    isRateAbove: 0.25,
    teachingNote: 'Obligatoire pour les sociétés réalisant plus de 840 000 € de CA. Bilan détaillé, compte de résultat complet, annexes. TVA déclarée mensuellement. Permet l\'optimisation fiscale fine (amortissements accélérés, provisions, crédits d\'impôt).',
  },
};

/**
 * Determine the default fiscal regime for a given legal form.
 */
export function getDefaultFiscalRegime(legalForm) {
  if (legalForm === 'auto') return 'micro';
  return 'simplifie';
}

/**
 * Detect a threshold APPROACH — fired when the company is between 80 % and
 * 100 % of a fiscal threshold. Returns a structured tip so the apprenant
 * can plan ahead before the mandatory bascule kicks in.
 *
 * Returns `null` when nothing relevant. Otherwise :
 *   { id, label, threshold, current, remaining, targetForm, targetRegime, reason }
 *
 * `id` is a stable identifier per (currentForm, targetForm) pair so the
 * Messagerie can dedupe via `triggerMilestone`.
 */
export function checkThresholdApproach(currentForm, currentRegime, ca12mGlissant) {
  // Auto-entrepreneur — seuil 188 700 €.
  if (currentForm === 'auto') {
    const threshold = 188700;
    if (ca12mGlissant >= threshold * 0.80 && ca12mGlissant < threshold) {
      const remaining = threshold - ca12mGlissant;
      return {
        id: 'approach-auto-ei',
        label: 'Seuil Auto-entrepreneur',
        threshold,
        current: ca12mGlissant,
        remaining,
        targetForm: 'ei',
        targetRegime: 'simplifie',
        reason:
          `Votre CA glissant 12 mois atteint ${Math.round(ca12mGlissant).toLocaleString('fr-FR')} € — vous êtes ` +
          `à moins de ${Math.round(remaining).toLocaleString('fr-FR')} € du plafond légal Auto-entrepreneur (188 700 €). ` +
          `Au-delà, la bascule en Entreprise Individuelle au régime Réel Simplifié devient obligatoire au 1er janvier suivant. ` +
          `Anticipez : ajustez vos modèles de facturation pour intégrer la TVA, ouvrez un compte bancaire pro dédié si ce n'est pas fait, ` +
          `et envisagez le passage volontaire vers EURL/SARL pour protéger votre patrimoine personnel.`,
      };
    }
  }
  // Régime simplifié → normal — seuil 840 000 €.
  if (currentRegime === 'simplifie') {
    const threshold = 840000;
    if (ca12mGlissant >= threshold * 0.80 && ca12mGlissant < threshold) {
      const remaining = threshold - ca12mGlissant;
      return {
        id: 'approach-simplifie-normal',
        label: 'Seuil régime Réel Simplifié',
        threshold,
        current: ca12mGlissant,
        remaining,
        targetForm: currentForm,
        targetRegime: 'normal',
        reason:
          `Votre CA glissant 12 mois atteint ${Math.round(ca12mGlissant).toLocaleString('fr-FR')} € — vous êtes ` +
          `à moins de ${Math.round(remaining).toLocaleString('fr-FR')} € du plafond du régime Réel Simplifié (840 000 €). ` +
          `Au-delà, le passage au régime Réel Normal devient obligatoire au 1er janvier suivant : déclaration TVA MENSUELLE, ` +
          `liasse fiscale plus détaillée, et clôture comptable plus rigoureuse. ` +
          `Anticipez : recrutez ou renforcez votre comptable (Sophie Lambert) et mettez en place un processus de clôture mensuelle dès maintenant.`,
      };
    }
  }
  return null;
}

/**
 * Check if the given legal form must evolve based on rolling 12-month CA.
 * Returns { mustChange: bool, targetForm: string|null, targetRegime: string|null, reason: string }.
 */
export function checkLegalFormEvolution(currentForm, currentRegime, ca12mGlissant) {
  // Auto-entrepreneur → bascule en EI au réel simplifié si CA > 188 700 €
  if (currentForm === 'auto' && ca12mGlissant > 188700) {
    return {
      mustChange: true,
      targetForm: 'ei',
      targetRegime: 'simplifie',
      reason: `Vous avez dépassé le seuil Auto-entrepreneur (188 700 € de CA glissant sur 12 mois — actuel : ${Math.round(ca12mGlissant).toLocaleString('fr-FR')} €). Bascule prévue en Entreprise Individuelle au régime Réel Simplifié au 1er janvier prochain.`,
    };
  }
  // Régime simplifié → réel normal si CA > 840 000 €
  if (currentRegime === 'simplifie' && ca12mGlissant > 840000) {
    return {
      mustChange: true,
      targetForm: currentForm,
      targetRegime: 'normal',
      reason: `Vous avez dépassé le seuil du régime Réel Simplifié (840 000 € de CA glissant — actuel : ${Math.round(ca12mGlissant).toLocaleString('fr-FR')} €). Bascule prévue en régime Réel Normal au 1er janvier prochain.`,
    };
  }
  return { mustChange: false, targetForm: null, targetRegime: null, reason: '' };
}

/**
 * Check eligibility for a manual legal form upgrade.
 */
export function canUpgradeTo(currentForm, targetForm) {
  const order = ['auto', 'ei', 'eurl', 'sarl', 'sas'];
  const ci = order.indexOf(currentForm);
  const ti = order.indexOf(targetForm);
  return ti > ci;
}

/**
 * Get the list of legal forms available at company creation (3 simplest).
 */
export function getStartupLegalForms() {
  return Object.values(LEGAL_FORMS).filter(lf => lf.startupAvailable);
}

/**
 * Get the list of legal forms accessible only via evolution.
 */
export function getEvolutionLegalForms() {
  return Object.values(LEGAL_FORMS).filter(lf => !lf.startupAvailable);
}
